import {Test, TestResult} from '@/shared/types/api';
import {BiomarkerStatus} from '@/shared/types/biomarker';
import {classifyBiomarker, type ClassifyParams, type SeverityTier} from '@/shared/biomarkers/classify';
import {resolveDisplayRule} from '@/shared/biomarkers/resolveDisplayRule';
import {ruleToConfig} from '@/shared/biomarkers/ruleToConfig';
import type {BiomarkerDisplayRule} from '@/shared/biomarkers/displayRuleTypes';

export interface ProcessedBiomarker {
    testName: string;
    result: string;
    units: string;
    refRange: string;
    abnormal?: string;
    statusDate: string;
    status: BiomarkerStatus;
    /** The band label when an admin-managed display rule matched, else the
     *  status string — this is what the UI should render. See classify.ts. */
    displayLabel: string;
    /** Canonical, color-aligned severity from the classifier (a matched
     *  display-rule band's own severity when it has one, else severityTier(status)).
     *  Use this — not severityTier(status) — for any count/bucket derivation
     *  so it can never disagree with the chip/zone color. See classify.ts. */
    tier: SeverityTier;
    progressBarPosition: number;
    loinc?: string | null;
    /** Health category the API resolved from the canon; see getTestCategory. */
    category?: string | null;
}

/**
 * Clean biomarker/test name by removing numerical prefixes from ELIS data
 * Examples:
 * - "01; Total IgE" -> "Total IgE"
 * - "02; WBC" -> "WBC"
 * - "Cholesterol" -> "Cholesterol" (unchanged)
 */
export const cleanBiomarkerName = (testName: string): string => {
    if (!testName) return testName;
    // Remove prefix pattern like "01; ", "02; ", etc.
    return testName.includes('; ') ? testName.split('; ')[1] : testName;
};

/** Canonical grouping/matching key for a biomarker: LOINC when present,
 *  else the cleaned lowercased name. Lets "Direct HDL" and "HDL" collapse
 *  (both loinc 2085-9) instead of being treated as different biomarkers. */
export function biomarkerGroupKey(r: {test_name?: string | null; loinc?: string | null}): string {
    if (r.loinc && r.loinc.trim().length > 0) return `loinc:${r.loinc.trim()}`;
    return `name:${cleanBiomarkerName(r.test_name ?? '')
        .toLowerCase()
        .trim()}`;
}

/**
 * Get the most recent biomarker from all test results
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const getLatestBiomarker = (
    tests: Test[],
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ProcessedBiomarker | null => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    if (validResults.length === 0) return null;

    // Sort by date descending to get the most recent (use statusDate for biomarker timing, fallback to amendedDate, then createdDate)
    const sortedResults = validResults.sort((a, b) => {
        // Get the test data to access amendedDate and createdDate
        const aTest = tests.find((test) => test.results.includes(a));
        const bTest = tests.find((test) => test.results.includes(b));

        const aDate = new Date(a.statusDate || aTest?.amendedDate || aTest?.createdDate || '').getTime();
        const bDate = new Date(b.statusDate || bTest?.amendedDate || bTest?.createdDate || '').getTime();

        return bDate - aDate;
    });

    const latestResult = sortedResults[0];
    return processBiomarker(latestResult, gender, rules);
};

/**
 * Get the top N most recent biomarkers for trends
 * @param tests - Array of tests
 * @param count - Number of biomarkers to return (default 4)
 * @param gender - Optional gender for gender-specific configs
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const getRecentBiomarkers = (
    tests: Test[],
    count: number = 4,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ProcessedBiomarker[] => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    // Group by canonical LOINC-first key (name fallback) so the same
    // biomarker from different sources/labels (e.g. "Direct HDL" vs "HDL",
    // both loinc 2085-9) collapses to a single entry.
    const biomarkerMap = new Map<string, TestResult>();

    // Sort by date descending first
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    // Keep only the most recent result for each unique test
    sortedResults.forEach((result) => {
        const key = biomarkerGroupKey(result);
        if (!biomarkerMap.has(key)) {
            biomarkerMap.set(key, result);
        }
    });

    // Convert to processed biomarkers and take the requested count
    return Array.from(biomarkerMap.values())
        .slice(0, count)
        .map((result) => processBiomarker(result, gender, rules));
};

/**
 * Format numeric result values by rounding to appropriate precision
 * Only shows decimal places if they are not zero
 */
export const formatResultValue = (value: string): string => {
    const numericValue = parseNumericValue(value);
    if (numericValue === null) return value; // Return original if not numeric

    // Round to 2 decimal places
    const rounded = Number(numericValue.toFixed(2));

    // If the number is a whole number, don't show decimal places
    if (rounded === Math.floor(rounded)) {
        return rounded.toString();
    }

    // Otherwise, show up to 2 decimal places (removing trailing zeros)
    return rounded.toString();
};

/**
 * A test is "not performed" when its result is absent or a non-numeric,
 * non-binary placeholder (e.g. "Test Not Performed", "No Result", "TNP").
 * The v8 design renders these as a muted "Not Tested" and drops them from
 * Lab Trends (there is no numeric trend to show). Numeric and binary
 * (Positive/Negative) results are considered performed.
 */
export const isTestNotPerformed = (result: string | null | undefined): boolean => {
    if (result == null) return true;
    const r = String(result).trim();
    if (r === '') return true;
    if (parseNumericValue(r) !== null) return false;
    if (isBinaryTest(r)) return false;
    return true;
};

/**
 * Check if a test result is a binary test (Positive/Negative)
 * Binary tests have string results like "Positive", "Negative", "Detected", "Not Detected"
 * @param result - The test result string or TestResult object
 */
export const isBinaryTest = (result: string | TestResult): boolean => {
    const resultValue = typeof result === 'string' ? result : result?.result;
    if (!resultValue) return false;

    const lower = resultValue.toLowerCase().trim();
    return (
        lower === 'positive' ||
        lower === 'postive' || // Handle common typo
        lower === 'negative' ||
        lower === 'detected' ||
        lower === 'not detected'
    );
};

/**
 * Get the status for a binary test result
 * @param result - The test result string
 * @returns 'Positive' or 'Negative' status
 */
export const getBinaryTestStatus = (result: string): 'Positive' | 'Negative' => {
    const lower = result.toLowerCase().trim();
    if (lower === 'positive' || lower === 'postive' || lower === 'detected') {
        return 'Positive';
    }
    return 'Negative';
};

/**
 * Process a single test result into a standardized biomarker format
 * @param result - The test result to process
 * @param gender - Optional gender for gender-specific configs (e.g., HDL Direct)
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const processBiomarker = (
    result: TestResult,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ProcessedBiomarker => {
    // Always attempt status determination (uses 1-100 default range when missing)
    const classification = classifyBiomarker(buildClassifyParams(result, gender, rules));

    return {
        testName: result.test_name,
        result: formatResultValue(result.result),
        units: result.units || '',
        refRange: result.refRange || '',
        abnormal: result.abnormal,
        statusDate: result.statusDate || '',
        status: classification.status,
        displayLabel: classification.displayLabel,
        tier: classification.tier,
        progressBarPosition: classification.indicatorPercent,
        loinc: result.loinc ?? null,
        category: result.category ?? null,
    };
};

// Build the classification inputs from a raw result row. Single place that
// maps a TestResult onto the shared classifier so determineStatus,
// calculateProgressBarPosition and the counts can never diverge.
//
// customConfig comes from the LOINC-first display-rule resolver when a rule
// matches; when it doesn't (empty `rules`, or no rule covers this assay),
// resolveDisplayRule returns null and classify.ts falls back to the lab
// range — the pre-migration behaviour.
const buildClassifyParams = (
    result: TestResult,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ClassifyParams => {
    const {min, max} = parseRefRangeWithDefault(result.refRange);
    const isBinary = isBinaryTest(result);
    const numeric = parseNumericValue(result.result);
    const rule = resolveDisplayRule(rules, {
        loinc: result.loinc,
        assayCode: result.test_code,
        testName: result.test_name,
        sex: gender,
    });
    return {
        rangeMin: min,
        rangeMax: max,
        rawRefRange: result.refRange ?? '',
        customConfig: rule ? ruleToConfig(rule) : null,
        isBinary,
        value: numeric ?? undefined,
        binaryStatus: isBinary ? getBinaryTestStatus(result.result) : null,
        abnormal: result.abnormal ?? null,
    };
};

/**
 * Determine the status (Normal, High, Low, Optimal, Borderline, Elevated) of a biomarker result.
 * Delegates to the shared config-first classifier (see classify.ts) so the discrete status,
 * the chart-zone color, the card chip and the count bucket always agree.
 */
export const determineStatus = (
    result: TestResult,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): BiomarkerStatus => {
    return classifyBiomarker(buildClassifyParams(result, gender, rules)).status;
};

/**
 * Calculate progress bar position (0-100) based on result and reference range
 * Priority order:
 * 1. API-provided reference range (primary source — standard 3-zone logic)
 * 2. Custom biomarker configurations (fallback when no API range available)
 * 3. Default 1-100 range
 *
 * Standard 3-zone:
 * - 0-33.33%: Low zone (orange)
 * - 33.33-66.67%: Normal/Optimal zone (green) - this is where the reference range maps to
 * - 66.67-100%: High zone (pink)
 *
 * Custom zones (e.g., Cortisol with 10%-80%-10%):
 * - 0-10%: Below Range
 * - 10-90%: In Range
 * - 90-100%: Above Range
 *
 * 4-zone layout (e.g., Total Cholesterol):
 * - 0-25%: Low
 * - 25-50%: Optimal
 * - 50-75%: Borderline
 * - 75-100%: High
 */
export const calculateProgressBarPosition = (
    result: TestResult,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): number => {
    return classifyBiomarker(buildClassifyParams(result, gender, rules)).indicatorPercent;
};

/**
 * Parse a numeric value from a result string
 */
export const parseNumericValue = (value: string): number | null => {
    if (!value) return null;

    // Remove common non-numeric characters and extract number
    const cleaned = value.replace(/[<>≤≥]/g, '').trim();
    const match = cleaned.match(/[\d.]+/);

    if (match) {
        const parsed = parseFloat(match[0]);
        return isNaN(parsed) ? null : parsed;
    }

    return null;
};

/**
 * Parse reference range string (e.g., "30-100", "< 5.7", "2.5 - 4.5", "<10-75")
 */
export const parseReferenceRange = (refRange: string): {min: number; max: number} | null => {
    if (!refRange) return null;

    // Handle different reference range formats. Normalize the various
    // dash characters that show up in lab data (ELIS in particular
    // sometimes returns ranges with an en-dash, e.g. "259–816" for
    // Total Testosterone) to a regular hyphen-minus so the range
    // regex below matches consistently. Without this, an en-dash
    // range silently falls through to the 0-100 default elsewhere
    // in the file, which paints in-range results as "High" on the
    // patient dashboard.
    const cleaned = refRange
        .replace(/[≤≥]/g, '')
        .replace(/[–—−]/g, '-') // en-dash, em-dash, minus sign → hyphen
        .trim();

    // Format: "min - max", "min-max", or "<min-max"
    const rangeMatch = cleaned.match(/<?\s*(\d+\.?\d*)\s*-\s*(\d+\.?\d*)/);
    if (rangeMatch) {
        const min = parseFloat(rangeMatch[1]);
        const max = parseFloat(rangeMatch[2]);
        if (!isNaN(min) && !isNaN(max)) {
            return {min, max};
        }
    }

    // Format: "< value" (upper limit only)
    const upperMatch = cleaned.match(/[<]\s*(\d+\.?\d*)/);
    if (upperMatch) {
        const max = parseFloat(upperMatch[1]);
        if (!isNaN(max)) {
            return {min: 0, max};
        }
    }

    // Format: "> value" (lower limit only)
    const lowerMatch = cleaned.match(/[>]\s*(\d+\.?\d*)/);
    if (lowerMatch) {
        const min = parseFloat(lowerMatch[1]);
        if (!isNaN(min)) {
            return {min, max: min * 2}; // Approximate upper bound
        }
    }

    return null;
};

/**
 * Parse reference range with default fallback
 * Returns default range {min: 0, max: 100} if range cannot be parsed
 */
export const parseRefRangeWithDefault = (
    refRange: string,
    defaultRange: {min: number; max: number} = {min: 0, max: 100}
): {min: number; max: number} => {
    return parseReferenceRange(refRange) || defaultRange;
};

/**
 * Format test name by extracting the display name from compound names
 * e.g., "12345; Glucose" -> "Glucose"
 */
export const formatTestName = (testName: string): string => {
    if (testName && testName.includes('; ')) {
        return testName.split('; ')[1];
    }
    return testName;
};

/**
 * Format date for display
 */
export const formatTestDate = (dateString: string): string => {
    try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit',
        });
    } catch {
        return dateString;
    }
};

/**
 * Calculate trend data for historical results of the same biomarker
 */
export const calculateBiomarkerTrend = (
    tests: Test[],
    testName: string
): Array<{name: string; value: number | null; dummy?: boolean}> => {
    const allResults = tests.flatMap((test) => test.results);
    const testResults = allResults
        .filter(
            (result) =>
                result.test_name === testName &&
                result.statusDate &&
                result.result &&
                result.result !== 'Pending' &&
                result.result !== 'N/A'
        )
        .sort((a, b) => new Date(a.statusDate!).getTime() - new Date(b.statusDate!).getTime());

    if (testResults.length === 0) return [];

    // Create chart data without padding
    const chartData: Array<{name: string; value: number | null; dummy?: boolean}> = [];

    // Take last 4 results for the trend
    const recentResults = testResults.slice(-4);

    recentResults.forEach((result) => {
        const numericValue = parseNumericValue(result.result);
        const dateLabel = formatTestDate(result.statusDate!);

        chartData.push({
            name: dateLabel,
            value: numericValue,
        });
    });

    return chartData;
};

/**
 * Calculate trend change for a biomarker (comparing latest vs previous result)
 */
export const calculateTrendChange = (
    tests: Test[],
    testName: string
): {changeText: string; changeType: 'up' | 'down'} => {
    const allResults = tests.flatMap((test) => test.results);
    const testResults = allResults
        .filter(
            (result) =>
                result.test_name === testName &&
                result.statusDate &&
                result.result &&
                result.result !== 'Pending' &&
                result.result !== 'N/A'
        )
        .sort((a, b) => new Date(a.statusDate!).getTime() - new Date(b.statusDate!).getTime());

    if (testResults.length < 2) {
        // If we only have one result or none, show the current value as neutral
        const currentValue = testResults.length > 0 ? testResults[testResults.length - 1].result : '0';
        return {
            changeText: currentValue,
            changeType: 'up', // Default to up for single values
        };
    }

    const latest = testResults[testResults.length - 1];
    const previous = testResults[testResults.length - 2];

    const latestValue = parseNumericValue(latest.result);
    const previousValue = parseNumericValue(previous.result);

    if (latestValue === null || previousValue === null) {
        return {
            changeText: latest.result,
            changeType: 'up',
        };
    }

    const change = latestValue - previousValue;
    const percentChange = previousValue !== 0 ? Math.abs((change / previousValue) * 100) : 0;

    return {
        changeText: `${percentChange.toFixed(1)}%`,
        changeType: change >= 0 ? 'up' : 'down',
    };
};

export interface MarkerStatusCounts {
    normal: number;
    borderline: number;
    critical: number;
}

/**
 * Categorize all biomarkers by their status for the markers overview chart
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const categorizeBiomarkerStatuses = (
    tests: Test[],
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): MarkerStatusCounts => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    // Get unique biomarkers (latest result for each test type)
    const biomarkerMap = new Map<string, TestResult>();
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    sortedResults.forEach((result) => {
        if (!biomarkerMap.has(result.test_name)) {
            biomarkerMap.set(result.test_name, result);
        }
    });

    // Categorize each unique biomarker
    let normal = 0;
    let borderline = 0;
    let critical = 0;

    biomarkerMap.forEach((result) => {
        // Exclude non-numeric, non-binary results (e.g. "Test Not Performed",
        // "No Result", "TNP") from the tally — a not-performed reading has no
        // status to count toward normal/borderline/critical and must not
        // inflate any bucket. Binary tests (Positive/Negative) are real
        // findings that also parse to null, so we keep them via isBinaryTest.
        if (parseNumericValue(result.result) === null && !isBinaryTest(result)) {
            return;
        }

        // Canonical, color-aligned tier so a biomarker's count bucket agrees with
        // its chip/zone color everywhere. Uses the classification's own tier
        // (via processBiomarker) rather than severityTier(status): a matched
        // display-rule band can carry a severity that diverges from its
        // label's default tier (e.g. HDL <40 labels "Low", whose default
        // severityTier is 'borderline', but the band's own severity is
        // 'critical'). This also retires the old 25%-magnitude
        // isSignificantlyOutOfRange split.
        const tier = processBiomarker(result, gender, rules).tier;
        if (tier === 'normal') {
            normal++;
        } else if (tier === 'critical') {
            critical++;
        } else {
            // 'borderline' and 'unknown' both fall here (unchanged default).
            borderline++;
        }
    });

    return {normal, borderline, critical};
};

/**
 * Determine if a result is significantly out of the normal range (critical vs borderline)
 */
export const isSignificantlyOutOfRange = (result: TestResult): boolean => {
    const numericResult = parseNumericValue(result.result);
    if (numericResult === null || !result.refRange) return false;

    const range = parseReferenceRange(result.refRange);
    if (!range) return false;

    const rangeWidth = range.max - range.min;
    const tolerance = rangeWidth * 0.25; // 25% outside range is considered critical

    // Check if result is significantly outside the range
    if (numericResult < range.min) {
        return range.min - numericResult > tolerance;
    }

    if (numericResult > range.max) {
        return numericResult - range.max > tolerance;
    }

    return false;
};

// OHD survey/operational measurement aliases that are NOT lab biomarkers
// (tobacco-use, BP/cholesterol/diabetes-meds yes-no answers, draw metadata,
// etc.). For OHD-sourced patients these arrive as result rows whose test_name
// falls through to the raw measurement_alias, so they must be filtered out of
// anywhere that lists "biomarkers" (e.g. the Select-a-Biomarker dropdown).
const OHD_NON_BIOMARKER_FIELDS = new Set<string>([
    'tobacco_use',
    'blood_pressure_meds',
    'chol_meds',
    'diabetes_meds',
    'diabetes_diagnosis',
    'pregnant',
    'glp_therapy',
    'hormone_replacement',
    'testosterone_replacement',
    'bp_erf',
    'biometrics',
    'draw_attempts',
    'draw_site',
    'draw_successful',
    'hrs_fasted',
]);

// Matches both the raw alias ("tobacco_use") and any title-cased display
// variant ("Tobacco Use") by normalizing to the snake_case alias form.
export const isNonBiomarkerField = (testName: string): boolean =>
    OHD_NON_BIOMARKER_FIELDS.has(testName.toLowerCase().trim().replace(/\s+/g, '_'));

/**
 * Get all unique biomarkers from test results (one per test name, most recent)
 * Returns biomarkers sorted by date (most recent first)
 * @param tests - Array of tests
 * @param gender - Optional gender for gender-specific configs
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const getAllUniqueBiomarkers = (
    tests: Test[],
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ProcessedBiomarker[] => {
    const allResults = tests.flatMap((test) => test.results);
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            result.test_name &&
            result.result !== 'Pending' &&
            result.result !== 'N/A' &&
            !isNonBiomarkerField(result.test_name)
    );

    // Group by canonical LOINC-first key (name fallback) so the same
    // biomarker from different sources/labels (e.g. "Direct HDL" vs "HDL",
    // both loinc 2085-9) collapses to a single entry — matches getRecentBiomarkers.
    const biomarkerMap = new Map<string, TestResult>();

    // Sort by date descending first
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    // Keep only the most recent result for each unique test
    sortedResults.forEach((result) => {
        const key = biomarkerGroupKey(result);
        if (!biomarkerMap.has(key)) {
            biomarkerMap.set(key, result);
        }
    });

    // Convert to processed biomarkers
    return Array.from(biomarkerMap.values()).map((result) => processBiomarker(result, gender, rules));
};

/**
 * Get a specific biomarker by test name (most recent result)
 * Returns the most recent result for the specified test name
 * @param tests - Array of tests
 * @param testName - The name of the test to find
 * @param gender - Optional gender for gender-specific configs
 * @param rules - Admin-managed display rules (see useDisplayRules); defaults to none
 */
export const getBiomarkerByName = (
    tests: Test[],
    testName: string,
    gender?: 'male' | 'female',
    rules: BiomarkerDisplayRule[] = []
): ProcessedBiomarker | null => {
    const allResults = tests.flatMap((test) => test.results);
    const cleanedSearchName = cleanBiomarkerName(testName);

    // Try exact match first, then try matching cleaned names
    const validResults = allResults.filter(
        (result) =>
            result.statusDate &&
            result.result &&
            (result.test_name === testName || cleanBiomarkerName(result.test_name) === cleanedSearchName) &&
            result.result !== 'Pending' &&
            result.result !== 'N/A'
    );

    if (validResults.length === 0) return null;

    // Sort by date descending to get the most recent
    const sortedResults = validResults.sort(
        (a, b) => new Date(b.statusDate!).getTime() - new Date(a.statusDate!).getTime()
    );

    return processBiomarker(sortedResults[0], gender, rules);
};
